import React, { useEffect, useState } from "react";
import VendorLayout from "@/components/vendor_portal/VendorLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { UploadCloud, CheckCircle2 } from "lucide-react";
import IdChip from "@/components/IdChip";

/**
 * Vendor Company Profile (Build Guide Phase 5).
 *
 * Real, editable form — vendor updates company details, categories,
 * territories, logo, catalog PDF. Persists via PATCH /api/vendor/me.
 * Reuses the same UploadCloud pattern from the public application form.
 */
export default function VendorCompanyProfilePage() {
  const [opts, setOpts] = useState({ product_categories: [], sales_territories: [] });
  const [vendor, setVendor] = useState(null);
  const [form, setForm] = useState(null);
  const [logoPhoto, setLogoPhoto] = useState(null);
  const [catalogPhoto, setCatalogPhoto] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCatalog, setUploadingCatalog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/public/vendors/options"),
      api.get("/vendor/me"),
    ])
      .then(([o, v]) => {
        setOpts(o.data);
        setVendor(v.data.vendor);
        setForm({
          company_name: v.data.vendor.company_name || "",
          website: v.data.vendor.website || "",
          primary_contact_name: v.data.vendor.primary_contact_name || "",
          phone: v.data.vendor.phone || "",
          business_address: v.data.vendor.business_address || "",
          years_in_business: v.data.vendor.years_in_business || "",
          product_categories: v.data.vendor.product_categories || [],
          sales_territories: v.data.vendor.sales_territories || [],
          company_description: v.data.vendor.company_description || "",
        });
        setLogoPhoto(v.data.vendor.logo_photo_id ? { photo_id: v.data.vendor.logo_photo_id, original_filename: "Current logo" } : null);
        setCatalogPhoto(v.data.vendor.catalog_photo_id ? { photo_id: v.data.vendor.catalog_photo_id, original_filename: "Current catalog" } : null);
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  }, []);

  if (!form) {
    return (
      <VendorLayout>
        <div className="overline">Loading…</div>
      </VendorLayout>
    );
  }

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };
  const toggleChip = (field, val) => {
    setSaved(false);
    setForm((f) => {
      const cur = new Set(f[field]);
      if (cur.has(val)) cur.delete(val); else cur.add(val);
      return { ...f, [field]: Array.from(cur) };
    });
  };

  const uploadFile = async (file, kind) => {
    const setBusy = kind === "logo" ? setUploadingLogo : setUploadingCatalog;
    setBusy(true); setSaved(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "logo") setLogoPhoto(data); else setCatalogPhoto(data);
    } catch (err) {
      setError(`Upload failed: ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true); setSaved(false);
    try {
      const payload = {
        ...form,
        website: form.website || null,
        years_in_business: form.years_in_business || null,
        logo_photo_id: logoPhoto?.photo_id || null,
        catalog_photo_id: catalogPhoto?.photo_id || null,
      };
      const { data } = await api.patch("/vendor/me", payload);
      setVendor(data.vendor);
      setSaved(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <VendorLayout>
      <section data-testid="vendor-profile-page">
        <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
          Company Profile
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-2">
          {vendor?.company_name || "Your Company"}
        </h1>
        <p className="mt-2 text-sm text-[#7A7A7A]">
          These details are shown to golf-shop staff when they browse the vendor marketplace.
        </p>

        {vendor?.vendor_id && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="vendor-profile-id-row">
            <IdChip
              value={vendor.vendor_id}
              label="Vendor ID"
              testId="vendor-profile-vendor-id"
            />
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#7A7A7A]">
              Share this with your club contact when they need to reference your account.
            </span>
          </div>
        )}

        {saved && (
          <div data-testid="vendor-profile-saved" className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#1A422D]/10 border border-[#1A422D]/30 text-[12px] uppercase tracking-[0.22em] font-semibold text-[#1A422D]">
            <CheckCircle2 size={14} /> Profile saved
          </div>
        )}
        {error && (
          <div className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">{error}</div>
        )}

        <form onSubmit={save} className="mt-10 space-y-8 max-w-4xl">
          <Grid2>
            <Input testId="vendor-profile-company" label="Company Name *" value={form.company_name} onChange={(v) => set("company_name", v)} required />
            <Input testId="vendor-profile-website" label="Website" value={form.website} onChange={(v) => set("website", v)} placeholder="https://" />
          </Grid2>

          <Grid2>
            <Input testId="vendor-profile-contact" label="Primary Contact Name *" value={form.primary_contact_name} onChange={(v) => set("primary_contact_name", v)} required />
            <Input testId="vendor-profile-phone" label="Phone *" value={form.phone} onChange={(v) => set("phone", v)} required />
          </Grid2>

          <Grid2>
            <Input testId="vendor-profile-years" label="Years in Business" value={form.years_in_business} onChange={(v) => set("years_in_business", v)} />
            <Input testId="vendor-profile-email-readonly" label="Login Email (read-only)" value={vendor?.email || ""} onChange={() => {}} disabled />
          </Grid2>

          <div>
            <Label>Business Address *</Label>
            <textarea
              data-testid="vendor-profile-address"
              value={form.business_address}
              onChange={(e) => set("business_address", e.target.value)}
              required
              rows={2}
              className="mt-2 input-field"
            />
          </div>

          <ChipGroup
            testId="vendor-profile-categories"
            label="Product Categories *"
            options={opts.product_categories}
            selected={form.product_categories}
            onToggle={(v) => toggleChip("product_categories", v)}
          />

          <ChipGroup
            testId="vendor-profile-territories"
            label="Sales Territories *"
            options={opts.sales_territories}
            selected={form.sales_territories}
            onToggle={(v) => toggleChip("sales_territories", v)}
          />

          <div>
            <Label>Company Description *</Label>
            <textarea
              data-testid="vendor-profile-description"
              value={form.company_description}
              onChange={(e) => set("company_description", e.target.value)}
              required
              minLength={10}
              rows={5}
              className="mt-2 input-field"
            />
          </div>

          <Grid2>
            <FileField testId="vendor-profile-logo" label="Company Logo" file={logoPhoto} onSelect={(f) => uploadFile(f, "logo")} busy={uploadingLogo} accept="image/*" hint="PNG or JPG · up to 5MB" />
            <FileField testId="vendor-profile-catalog" label="Catalog PDF" file={catalogPhoto} onSelect={(f) => uploadFile(f, "catalog")} busy={uploadingCatalog} accept="application/pdf" hint="PDF · up to 50MB" />
          </Grid2>

          <div className="pt-4">
            <button
              type="submit"
              disabled={saving || uploadingLogo || uploadingCatalog}
              data-testid="vendor-profile-save"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </form>
      </section>
    </VendorLayout>
  );
}

function Label({ children }) {
  return <label className="block text-[12px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A]">{children}</label>;
}
function Grid2({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>; }
function Input({ testId, label, value, onChange, required, placeholder, disabled }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        disabled={disabled}
        className="mt-2 input-field disabled:bg-[#F0EDE0] disabled:text-[#7A7A7A]"
      />
    </div>
  );
}
function ChipGroup({ testId, label, options, selected, onToggle }) {
  return (
    <div>
      <Label>{label}</Label>
      <div data-testid={testId} className="mt-2 flex flex-wrap gap-2">
        {options.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              data-testid={`${testId}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-4 py-2 border text-sm transition-colors ${on ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]" : "bg-white text-[#4A4A4A] border-[#E5E3D8] hover:border-[#1A422D]"}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
function FileField({ testId, label, file, onSelect, busy, accept, hint }) {
  return (
    <div>
      <Label>{label}</Label>
      <label
        data-testid={`${testId}-dropzone`}
        className="mt-2 flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-[#B59A5A]/50 bg-[#FAF9F2] cursor-pointer hover:border-[#1A422D] transition-colors"
      >
        <input
          type="file"
          accept={accept}
          data-testid={testId}
          onChange={(e) => e.target.files?.[0] && onSelect(e.target.files[0])}
          className="hidden"
          disabled={busy}
        />
        {file ? (
          <>
            <CheckCircle2 size={22} className="text-[#1A422D]" />
            <span className="text-sm text-[#1A422D] font-semibold">{file.original_filename || "Uploaded"}</span>
            <span className="text-xs text-[#7A7A7A]">Click to replace</span>
          </>
        ) : (
          <>
            <UploadCloud size={22} className="text-[#B59A5A]" />
            <span className="text-sm text-[#4A4A4A]">{busy ? "Uploading…" : "Click to upload"}</span>
            <span className="text-xs text-[#7A7A7A]">{hint}</span>
          </>
        )}
      </label>
    </div>
  );
}
